import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { useEffect, useMemo, useState } from 'react'
import { BarraDeContatos } from '../../components/AcoesEmContatos'
import { Avatar } from '../../components/Avatar'
import { BotaoAcao } from '../../components/BotaoAcao'
import { ExportarContatos } from '../../components/ExportarContatos'
import { FilterBar, FilterSelect } from '../../components/filters'
import { ImportarContatos } from '../../components/ImportarContatos'
import { IconContacts, IconExport, IconImport, IconSearch } from '../../components/icons'
import { ChipStatus } from '../../components/LeadPanel'
import { fullName, shortTime } from '../../lib/format'
import { t, tRico } from '../../lib/i18n'
import { accountColor } from '../../lib/palette'
import { useWorkspaceRole } from '../../lib/role'
import { useTRPC } from '../../lib/trpc'

export const Route = createFileRoute('/_app/contatos')({ component: Contacts })

/**
 * Mesmos recortes do relatório, em dia-calendário de Brasília. Rótulos em
 * português e traduzidos na hora de montar as opções — constante de módulo
 * traduzida ficaria presa no idioma de abertura.
 */
const PERIODOS = [
  { key: 'hoje' as const, label: 'Hoje' },
  { key: 'ontem' as const, label: 'Ontem' },
  { key: '7d' as const, label: '7 dias' },
  { key: '30d' as const, label: '30 dias' },
]

type PeriodoKey = (typeof PERIODOS)[number]['key']

function Contacts() {
  const trpc = useTRPC()
  const [search, setSearch] = useState('')
  const [period, setPeriod] = useState<PeriodoKey | null>(null)
  const [inboxId, setInboxId] = useState<string | null>(null)
  const [tagIds, setTagIds] = useState<string[]>([])
  const [campaignIds, setCampaignIds] = useState<string[]>([])
  const [channelIds, setChannelIds] = useState<string[]>([])
  const [status, setStatus] = useState<'active' | 'blocked' | null>(null)
  const [importando, setImportando] = useState(false)
  const [exportando, setExportando] = useState(false)
  const [marcados, setMarcados] = useState<Set<string>>(new Set())
  /** "Todos os N do filtro" — o conjunto é o recorte, não uma lista de ids. */
  const [todosDoFiltro, setTodosDoFiltro] = useState(false)
  const { isAdmin } = useWorkspaceRole()
  const workspaceId =
    typeof window === 'undefined' ? null : localStorage.getItem('tipscore:workspace')

  const inboxes = useQuery(trpc.inbox.list.queryOptions())
  const tags = useQuery(trpc.lead.tags.queryOptions())
  const channels = useQuery(trpc.channel.list.queryOptions())
  const campaigns = useQuery(trpc.campaign.list.queryOptions({}))

  const [page, setPage] = useState(0)

  // A busca agora é do servidor (com paginação, filtrar só a página daria
  // resultado errado), mas não a cada tecla: 300ms segura a rajada de digitação
  // sem a pessoa sentir espera.
  const [buscaAplicada, setBuscaAplicada] = useState('')
  useEffect(() => {
    const id = setTimeout(() => setBuscaAplicada(search.trim()), 300)
    return () => clearTimeout(id)
  }, [search])

  const filtrosAtivos = useMemo(
    () => ({
      ...(buscaAplicada ? { search: buscaAplicada } : {}),
      ...(period ? { period } : {}),
      ...(inboxId ? { inboxIds: [inboxId] } : {}),
      ...(tagIds.length ? { tagIds } : {}),
      ...(campaignIds.length ? { campaignIds } : {}),
      ...(channelIds.length ? { channelIds } : {}),
      ...(status ? { status } : {}),
    }),
    [buscaAplicada, period, inboxId, tagIds, campaignIds, channelIds, status],
  )

  // Mudou o recorte, a página 3 do recorte antigo não quer dizer nada.
  const [filtrosAnteriores, setFiltrosAnteriores] = useState(filtrosAtivos)
  if (filtrosAnteriores !== filtrosAtivos) {
    setFiltrosAnteriores(filtrosAtivos)
    if (page !== 0) setPage(0)
  }

  const leads = useQuery({
    ...trpc.lead.list.queryOptions({ ...filtrosAtivos, page }),
    // Sem isto a lista pisca em "Carregando…" a cada página; com isto, a
    // página anterior fica na tela até a nova chegar.
    placeholderData: (anterior) => anterior,
  })

  const ativos =
    (period ? 1 : 0) +
    (inboxId ? 1 : 0) +
    tagIds.length +
    campaignIds.length +
    channelIds.length +
    (status ? 1 : 0)

  const items = leads.data?.items ?? []
  const total = leads.data?.total ?? 0
  const pageSize = leads.data?.pageSize ?? 30
  const ultimaPagina = Math.max(0, Math.ceil(total / pageSize) - 1)

  return (
    <div className="h-full overflow-y-auto">
      <div className="fim-seguro mx-auto max-w-3xl px-5 pt-4 md:pt-8">
        {/* Título e ações na mesma linha; o subtítulo embaixo, atravessando a
            página inteira. Com o subtítulo dentro da mesma coluna do título,
            ele dividia a largura com os botões e ficava espremido no celular. */}
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="min-w-0 flex-1 font-display text-2xl">{t('Contatos')}</h1>

          {/* Só administrador: importar cria milhares de contatos e exportar
              leva a base inteira para fora do CRM. */}
          {isAdmin && (
            <div className="flex flex-none gap-2">
              <BotaoAcao onClick={() => setImportando(true)}>
                <IconImport className="mr-1 inline size-3.5 align-[-2px]" />
                {t('Importar')}
              </BotaoAcao>
              <BotaoAcao onClick={() => setExportando(true)}>
                <IconExport className="mr-1 inline size-3.5 align-[-2px]" />
                {t('Exportar')}
              </BotaoAcao>
            </div>
          )}
        </div>

        <p className="mt-1 text-xs leading-relaxed text-ink-2">
          {t(
            'Todo mundo que já escreveu para alguma conta conectada. Uma pessoa aparece uma vez só, mesmo falando com Comercial e Suporte.',
          )}
        </p>

        <label className="relative mt-5 flex items-center">
          <span className="sr-only">{t('Buscar contato')}</span>
          <IconSearch className="pointer-events-none absolute left-2.5 size-[15px] text-ink-3" />
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t('Buscar por nome, @usuário, telefone ou ID do Telegram')}
            className="w-full rounded-md border border-line bg-surface-2 py-2 pl-8 pr-3 text-sm placeholder:text-ink-3 hover:border-line-strong focus-visible:bg-surface"
          />
        </label>

        <FilterBar
          active={ativos}
          onClear={() => {
            setPeriod(null)
            setInboxId(null)
            setTagIds([])
            setCampaignIds([])
            setChannelIds([])
            setStatus(null)
          }}
          // Mais de uma tag é interseção: quem não souber disso leria
          // "0 contatos" como lista vazia, e não como filtro estreito.
          note={
            tagIds.length > 1 ? (
              <span>{t('Mostrando quem tem todas as tags marcadas.')}</span>
            ) : null
          }
        >
          <FilterSelect
            label={t('Atividade')}
            placeholder={t('Qualquer data')}
            options={PERIODOS.map((opcao) => ({ value: opcao.key, label: t(opcao.label) }))}
            values={period ? [period] : []}
            onChange={([valor]) => setPeriod((valor as PeriodoKey) ?? null)}
          />

          <FilterSelect
            label={t('Inbox')}
            placeholder={t('Todas')}
            options={(inboxes.data ?? []).map((row, index) => ({
              value: row.id,
              label: row.name,
              dot: accountColor(index),
            }))}
            values={inboxId ? [inboxId] : []}
            onChange={([valor]) => setInboxId(valor ?? null)}
          />

          <FilterSelect
            label={t('Tags')}
            placeholder={t('Todas')}
            multiple
            options={(tags.data ?? []).map((tag) => ({
              value: tag.id,
              label: tag.label,
              dot: tag.color,
            }))}
            values={tagIds}
            onChange={setTagIds}
          />

          {/* "Ativo" aqui quer dizer "não marcado como bloqueado" — o Telegram
              não confirma que alguém está ativo, só revela o bloqueio quando
              uma tentativa de envio falha. Daí o rótulo do vazio ser "Todos" e
              não "Ativos": sem filtro, a lista não afirma nada sobre status. */}
          <FilterSelect
            label={t('Conta')}
            placeholder={t('Todas')}
            multiple
            options={(channels.data ?? []).map((linha, indice) => ({
              value: linha.id,
              label: linha.name,
              dot: accountColor(indice),
            }))}
            values={channelIds}
            onChange={setChannelIds}
          />

          <FilterSelect
            label={t('Campanha')}
            placeholder={t('Todas')}
            multiple
            options={(campaigns.data ?? []).map((linha) => ({
              value: linha.id,
              label: linha.label,
            }))}
            values={campaignIds}
            onChange={setCampaignIds}
          />

          <FilterSelect
            label={t('Status')}
            placeholder={t('Todos')}
            options={[
              { value: 'active', label: t('Ativo'), dot: 'var(--accent)' },
              { value: 'blocked', label: t('Bloqueou o bot'), dot: 'var(--danger)' },
            ]}
            values={status ? [status] : []}
            onChange={([valor]) => setStatus((valor as 'active' | 'blocked') ?? null)}
          />
        </FilterBar>

        {leads.isPending && <p className="mt-6 text-xs text-ink-3">{t('Carregando…')}</p>}

        {!leads.isPending && items.length === 0 && (
          <div className="mt-6 rounded-lg border border-dashed border-line-strong px-4 py-10 text-center">
            <IconContacts className="mx-auto size-6 text-ink-3" />
            <p className="mt-2 text-sm text-ink-2">
              {search
                ? t('Nenhum contato para essa busca.')
                : ativos > 0
                  ? t('Nenhum contato com esses filtros.')
                  : t('Nenhum contato ainda.')}
            </p>
            {!search && ativos === 0 && (
              <p className="mt-1 text-2xs text-ink-3">
                {t('Contatos aparecem conforme as mensagens chegam — o CRM não importa histórico.')}
              </p>
            )}
          </div>
        )}

        {/* Em cima do começo da lista, não no cabeçalho da página: a seleção
            é sobre as linhas, e o controle dela mora junto delas. */}
        {isAdmin && items.length > 0 && (
          <BarraDeContatos
            marcados={marcados}
            naPagina={items.map((linha) => linha.id)}
            total={total}
            todosDoFiltro={todosDoFiltro}
            filtros={filtrosAtivos}
            onMarcarPagina={(marcar) => {
              setTodosDoFiltro(false)
              setMarcados(marcar ? new Set(items.map((linha) => linha.id)) : new Set())
            }}
            onSelecionarTudo={() => {
              setTodosDoFiltro(true)
              setMarcados(new Set(items.map((linha) => linha.id)))
            }}
            onLimpar={() => {
              setTodosDoFiltro(false)
              setMarcados(new Set())
            }}
            onPronto={() => {
              setTodosDoFiltro(false)
              setMarcados(new Set())
              leads.refetch()
            }}
          />
        )}

        <ul className="mt-4 flex flex-col gap-1.5">
          {items.map((row) => {
            const name = fullName(row)
            const conteudo = (
              <>
                <Avatar
                  leadId={row.id}
                  name={name}
                  hasPhoto={row.hasPhoto}
                  className="size-9 flex-none rounded-full text-xs"
                />
                <span className="min-w-0 flex-1">
                  <span className="flex items-baseline gap-2">
                    <span className="truncate text-sm font-semibold">{name}</span>
                    {row.username && (
                      <span className="truncate text-2xs text-ink-3">@{row.username}</span>
                    )}
                  </span>
                  <span className="flex flex-wrap items-baseline gap-x-2 text-2xs text-ink-3">
                    {row.phone && <span>{row.phone}</span>}
                    {/* Tabular para os dígitos alinharem entre as linhas — é
                        número que se compara de relance, não texto. */}
                    <span className="tabular-nums">ID {row.telegramUserId}</span>
                  </span>
                </span>
                {/* Antes da hora e não antes do ID: ali ele disputava com um
                    número que se lê de relance. Aqui fica na coluna das
                    informações de estado, que é onde o olho procura. */}
                {/* Antes do status, como pedido: "de onde veio" é o contexto
                    que muda como se lê o resto da linha. */}
                {row.importedAt && (
                  <span
                    className="inline-flex flex-none items-center rounded-full bg-surface-3 px-2 py-0.5 text-2xs font-bold text-ink-2"
                    title={
                      row.importedFrom
                        ? t('Importado de {origem} em {quando}', {
                            origem: row.importedFrom,
                            quando: shortTime(row.importedAt),
                          })
                        : t('Importado em {quando}', { quando: shortTime(row.importedAt) })
                    }
                  >
                    {t('Importado')}
                  </span>
                )}

                <ChipStatus status={row.status} desde={row.statusChangedAt} />

                <span className="flex-none text-2xs text-ink-3">
                  {shortTime(row.lastActivityAt)}
                </span>
              </>
            )

            const marcada = marcados.has(row.id)

            /**
             * A caixa fica **fora** do link, na mesma linha.
             *
             * Assim marcar e abrir a conversa convivem: um alvo para cada,
             * sem modo que troque o significado do clique no meio da lista.
             */
            return (
              <li
                key={row.id}
                className={`flex items-center gap-2 rounded-lg border px-2 ${
                  marcada
                    ? 'border-accent-soft-line bg-accent-soft'
                    : 'border-line bg-surface hover:border-line-strong'
                }`}
              >
                {isAdmin && (
                  <input
                    type="checkbox"
                    checked={marcada}
                    onChange={() => {
                      setTodosDoFiltro(false)
                      setMarcados((atual) => {
                        const proximo = new Set(atual)
                        if (proximo.has(row.id)) proximo.delete(row.id)
                        else proximo.add(row.id)
                        return proximo
                      })
                    }}
                    aria-label={t('Marcar {nome}', { nome: name })}
                    className="size-3.5 flex-none"
                  />
                )}

                {row.conversationId ? (
                  <Link
                    to="/inbox"
                    search={{ c: row.conversationId }}
                    className="flex min-w-0 flex-1 items-center gap-3 py-2.5 text-left"
                  >
                    {conteudo}
                  </Link>
                ) : (
                  // Lead sem conversa visível não vira link: clicar levaria a
                  // uma tela vazia, e link que não leva a lugar nenhum é pior
                  // do que linha inerte.
                  <div className="flex min-w-0 flex-1 items-center gap-3 py-2.5">{conteudo}</div>
                )}
              </li>
            )
          })}
        </ul>

        {total > pageSize && (
          <nav
            className="mt-5 flex items-center justify-between gap-3 border-t border-line pt-4"
            aria-label={t('Paginação dos contatos')}
          >
            {/* Quantos e quais: "31–60 de 214" responde onde a pessoa está sem
                ela precisar multiplicar página por tamanho de página. */}
            <p className="text-2xs text-ink-3">
              {tRico(total === 1 ? '{intervalo} de {n} contato' : '{intervalo} de {n} contatos', {
                intervalo: (
                  <strong className="font-semibold text-ink-2">
                    {page * pageSize + 1}–{Math.min((page + 1) * pageSize, total)}
                  </strong>
                ),
                n: total,
              })}
            </p>

            <span className="flex items-center gap-1.5">
              <button
                type="button"
                onClick={() => setPage((atual) => Math.max(0, atual - 1))}
                disabled={page === 0 || leads.isFetching}
                className="rounded-md border border-line px-2.5 py-1.5 text-2xs font-semibold text-ink-2 hover:border-line-strong hover:bg-surface-2 disabled:opacity-40"
              >
                {t('Anterior')}
              </button>
              <span className="px-1 text-2xs text-ink-3">
                {page + 1} / {ultimaPagina + 1}
              </span>
              <button
                type="button"
                onClick={() => setPage((atual) => Math.min(ultimaPagina, atual + 1))}
                disabled={page >= ultimaPagina || leads.isFetching}
                className="rounded-md border border-line px-2.5 py-1.5 text-2xs font-semibold text-ink-2 hover:border-line-strong hover:bg-surface-2 disabled:opacity-40"
              >
                {t('Próxima')}
              </button>
            </span>
          </nav>
        )}
        {importando && <ImportarContatos onClose={() => setImportando(false)} />}
        {exportando && (
          <ExportarContatos
            workspaceId={workspaceId}
            filtrosAtuais={{ status, channelId: channelIds[0] ?? null }}
            onClose={() => setExportando(false)}
          />
        )}
      </div>
    </div>
  )
}
